'use client';

import * as React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';

interface CodeAccordionProps {
  htmlCode: string;
  cssCode: string;
  jsCode: string;
  htmlInstructions: string;
  cssInstructions: string;
  jsInstructions: string;
  onHtmlCodeChange: (value: string) => void;
  onCssCodeChange: (value: string) => void;
  onJsCodeChange: (value: string) => void;
  onHtmlInstructionsChange: (value: string) => void;
  onCssInstructionsChange: (value: string) => void;
  onJsInstructionsChange: (value: string) => void;
}

export function CodeAccordion({
  htmlCode,
  cssCode,
  jsCode,
  htmlInstructions,
  cssInstructions,
  jsInstructions,
  onHtmlCodeChange,
  onCssCodeChange,
  onJsCodeChange,
  onHtmlInstructionsChange,
  onCssInstructionsChange,
  onJsInstructionsChange,
}: CodeAccordionProps) {
  const [expanded, setExpanded] = React.useState<{
    html: boolean;
    css: boolean;
    js: boolean;
  }>({
    html: true, // HTML expandido por padrão
    css: false,
    js: false,
  });

  const toggleSection = (section: 'html' | 'css' | 'js') => {
    setExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sections = [
    {
      id: 'html' as const,
      label: 'HTML',
      icon: 'ri-html5-line',
      code: htmlCode,
      instructions: htmlInstructions,
      onCodeChange: onHtmlCodeChange,
      onInstructionsChange: onHtmlInstructionsChange,
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
    },
    {
      id: 'css' as const,
      label: 'CSS',
      icon: 'ri-css3-line',
      code: cssCode,
      instructions: cssInstructions,
      onCodeChange: onCssCodeChange,
      onInstructionsChange: onCssInstructionsChange,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800',
    },
    {
      id: 'js' as const,
      label: 'JavaScript',
      icon: 'ri-javascript-line',
      code: jsCode,
      instructions: jsInstructions,
      onCodeChange: onJsCodeChange,
      onInstructionsChange: onJsInstructionsChange,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
    },
  ];

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const isExpanded = expanded[section.id];
        const hasContent = section.code.trim() !== '';

        return (
          <div
            key={section.id}
            className={cn(
              'border rounded-lg overflow-hidden transition-all duration-200',
              isExpanded
                ? `${section.borderColor} shadow-md`
                : 'border-gray-200 dark:border-[#8a2be2]/30'
            )}
          >
            {/* Header */}
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className={cn(
                'w-full flex items-center justify-between p-4 transition-colors duration-200',
                isExpanded && section.bgColor,
                !isExpanded && 'hover:bg-gray-50 dark:hover:bg-white/5'
              )}
            >
              <div className="flex items-center space-x-3">
                <i className={cn(section.icon, 'text-xl', section.color)}></i>
                <span className={cn('font-semibold font-ubuntu', section.color)}>
                  {section.label}
                </span>
                {!hasContent && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-montserrat">
                    (Opcional)
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {hasContent && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-montserrat">
                    Preenchido
                  </span>
                )}
                <i
                  className={cn(
                    'ri-arrow-down-s-line text-xl transition-transform duration-200',
                    section.color,
                    isExpanded && 'rotate-180'
                  )}
                ></i>
              </div>
            </button>

            {/* Content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4">
                    {/* Instructions Field */}
                    <div>
                      <label
                        htmlFor={`${section.id}-instructions`}
                        className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat"
                      >
                        Instruções de uso ({section.label})
                        <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">
                          (opcional)
                        </span>
                      </label>
                      <textarea
                        id={`${section.id}-instructions`}
                        value={section.instructions}
                        onChange={(e) => section.onInstructionsChange(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] transition-colors duration-200 font-montserrat text-sm resize-none"
                        placeholder={`Ex: Cole o código ${section.label} em um widget de HTML no rodapé da página`}
                      />
                    </div>

                    {/* Code Field */}
                    <div>
                      <label
                        htmlFor={`${section.id}-code`}
                        className="block text-sm font-medium text-gray-700 dark:text-[#e0e1dd] mb-2 font-montserrat"
                      >
                        Código {section.label}
                        {!hasContent && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">
                            (opcional)
                          </span>
                        )}
                      </label>
                      <textarea
                        id={`${section.id}-code`}
                        value={section.code}
                        onChange={(e) => section.onCodeChange(e.target.value)}
                        rows={12}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-[#8a2be2]/30 rounded-lg focus:ring-2 focus:ring-[#8a2be2] focus:border-[#8a2be2] dark:bg-[#0d0d0d]/50 dark:text-[#e0e1dd] font-mono text-sm transition-colors duration-200 resize-none"
                        placeholder={`Digite ou cole o código ${section.label} aqui...`}
                      />
                      {!hasContent && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 font-montserrat">
                          Este campo é opcional. Se não preencher, será exibido como "{section.label} opcional" na visualização.
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

